import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { setDocumentSeo } from "@/lib/seo";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ProductGallery } from "@/components/ProductGallery";
import { ShareButton } from "@/components/ShareButton";
import { Minus, Plus, Heart, Gift, PenLine, Truck, Sparkles, QrCode } from "lucide-react";

const RECIPIENTS = ["Female", "Male", "Child"];
const RECIPIENT_LABELS = { Female: "For Her (Female)", Male: "For Him (Male)", Child: "For a Child" };

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const { localEstimate } = useCurrency();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState("");
  const [recipient, setRecipient] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [childGender, setChildGender] = useState("");
  const [message, setMessage] = useState("");
  const [addons, setAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);

  useEffect(() => {
    setP(null); setRecipient(""); setRecipientName(""); setChildGender(""); setQty(1); setMessage(""); setSelectedAddons([]);
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data); setActiveImg(r.data.image || "");
      setDocumentSeo(r.data.name, (r.data.short_desc || r.data.description || "").slice(0, 160), r.data.image);
      const email = localStorage.getItem("gh_email");
      if (email) api.post("/track/view", { email, product: r.data.name, slug: r.data.slug, origin: window.location.origin }).catch(() => {});
    }).catch(() => setP(false));
  }, [slug]);

  useEffect(() => { api.get("/addons").then((r) => setAddons(r.data)).catch(() => {}); }, []);

  if (p === false) return <div className="max-w-3xl mx-auto px-5 py-32 text-center text-[#8A8175]">This gift couldn't be found. <Link to="/shop" className="text-[#8E9A86] underline">Back to shop</Link></div>;
  if (!p) return <div className="py-32 text-center text-[#8A8175]">Loading…</div>;

  const isBox = p.product_type === "box";
  const gallery = [p.image, ...(p.images || [])].filter(Boolean);
  const recipientValue = recipient === "Child" && childGender ? `Child (${childGender})` : recipient;

  const toggleAddon = (id) => setSelectedAddons((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const validate = () => {
    if (isBox && !recipient) { toast.error("Please choose who this gift is for."); return false; }
    if (isBox && recipient === "Child" && !childGender) { toast.error("Please choose if the child is a boy or girl."); return false; }
    return true;
  };
  const commit = () => {
    addItem(p, qty, isBox ? recipientValue : null, isBox ? (message.trim() || null) : null, isBox ? (recipientName.trim() || null) : null);
    if (isBox) addons.filter((a) => selectedAddons.includes(a.id)).forEach((a) => addItem(a, 1));
  };
  const add = () => { if (!validate()) return; commit(); toast.success(`${p.name} added with love.`); };
  const buyNow = () => { if (!validate()) return; commit(); nav("/cart"); };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
      <Link to="/shop" className="text-sm text-[#8A8175] hover:text-[#8E9A86]">← Back to shop</Link>
      <div className="grid lg:grid-cols-2 gap-12 mt-6">
        <ProductGallery name={p.name} activeImg={activeImg} gallery={gallery} onSelect={setActiveImg} />
        <div className="gh-fade-up" style={{ animationDelay: "100ms" }}>
          {p.badge && <span className="bg-[#D9CBA0] text-[#3A3632] text-xs font-semibold px-3 py-1 rounded-full">{p.badge}</span>}
          <h1 className="font-heading text-4xl sm:text-5xl text-[#3A3632] mt-4">{p.name}</h1>
          <div className="text-2xl text-[#8E9A86] font-semibold mt-3">{money(p.price)}{localEstimate(p.price) && <span className="text-sm text-[#8A8175] font-normal ml-2" data-testid="product-local-price">≈ {localEstimate(p.price)}</span>}</div>
          <p className="text-[#6B655F] mt-6 leading-relaxed text-lg">{p.description}</p>

          {p.reflection && (
            <div className="border-l-3 border-[#D9CBA0] bg-[#C4A4A4]/8 rounded-r-xl p-5 mt-6" style={{ borderLeftWidth: "3px" }} data-testid="product-reflection">
              <span className="gh-overline text-[10px] text-[#8E9A86]">A little reflection</span>
              <p className="font-hand text-2xl text-[#3A3632] leading-snug mt-1.5">"{p.reflection}"</p>
            </div>
          )}

          <div className="bg-[#F2EFEB] rounded-xl p-5 mt-7" data-testid="hope-card-info">
            <div className="flex items-center gap-2 text-[#3A3632] font-medium"><PenLine className="h-5 w-5 text-[#C4A4A4]" /> Every box includes our signature Hope Card</div>
            <ul className="mt-3 space-y-2 text-sm text-[#6B655F]">
              <li className="flex items-start gap-2"><Heart className="h-4 w-4 text-[#8E9A86] mt-0.5 shrink-0" /> Your personal message, hand-written in a keepsake script</li>
              <li className="flex items-start gap-2"><Sparkles className="h-4 w-4 text-[#8E9A86] mt-0.5 shrink-0" /> A small prayer or encouraging reflection, when it fits</li>
              <li className="flex items-start gap-2"><QrCode className="h-4 w-4 text-[#8E9A86] mt-0.5 shrink-0" /> A QR code to a short welcome video from Hope &amp; Favor</li>
            </ul>
          </div>

          {isBox && (
            <div className="mt-6">
              <label htmlFor="recipient-select" className="text-sm font-medium text-[#3A3632] block mb-2">Who is this gift for?</label>
              <select id="recipient-select" value={recipient} onChange={(e) => { setRecipient(e.target.value); setChildGender(""); }} data-testid="recipient-select"
                className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 bg-white outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30">
                <option value="">Please select…</option>
                {RECIPIENTS.map((r) => <option key={r} value={r}>{RECIPIENT_LABELS[r]}</option>)}
              </select>
              {recipient === "Child" && (
                <div className="mt-3">
                  <label htmlFor="child-gender-select" className="text-sm font-medium text-[#3A3632] block mb-2">Is the child a boy or girl? <span className="text-[#8A8175] font-normal">(under 18)</span></label>
                  <select id="child-gender-select" value={childGender} onChange={(e) => setChildGender(e.target.value)} data-testid="child-gender-select"
                    className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 bg-white outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30">
                    <option value="">Please select…</option>
                    <option value="Boy">Boy</option>
                    <option value="Girl">Girl</option>
                  </select>
                </div>
              )}
              <div className="mt-3">
                <label htmlFor="recipient-name" className="text-sm font-medium text-[#3A3632] block mb-2">Recipient's name <span className="text-[#8A8175] font-normal">(optional — appears on the Hope Card)</span></label>
                <input id="recipient-name" type="text" value={recipientName} maxLength={60} onChange={(e) => setRecipientName(e.target.value)} data-testid="recipient-name-input"
                  placeholder="e.g. Sarah" className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 bg-white outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" />
                {recipientName.trim() && <p className="text-xs text-[#8A8175] mt-1" data-testid="recipient-name-preview">The card will read “For {recipientName.trim()}”.</p>}
              </div>
            </div>
          )}

          {isBox && (
            <div className="mt-6">
              <label htmlFor="personal-message" className="text-sm font-medium text-[#3A3632] block mb-2">Your personal message <span className="text-[#8A8175] font-normal">(hand-written on the Hope Card)</span></label>
              <textarea id="personal-message" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="personal-message-input"
                rows={5} maxLength={1000} placeholder="Write from your heart… we'll hand-letter and center your words onto the card."
                className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 bg-white outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30 resize-y" />
              <div className="text-right text-xs text-[#8A8175] mt-1" data-testid="message-char-count">{message.length}/1000</div>
              {message.trim() && (
                <div className="mt-2 bg-[#FAF7F2] border border-[#D9CBA0]/50 rounded-lg p-4" data-testid="message-preview">
                  <span className="gh-overline text-[10px]">Preview</span>
                  <p className="font-hand text-2xl text-[#3A3632] leading-snug mt-1">{message}</p>
                </div>
              )}
            </div>
          )}

          {isBox && addons.length > 0 && (
            <div className="mt-6" data-testid="addons-section">
              <div className="text-sm font-medium text-[#3A3632] mb-1">Make it extra special <span className="text-[#8A8175] font-normal">(optional)</span></div>
              <div className="flex items-center gap-2 text-xs text-[#8E9A86] mb-3"><Heart className="h-3.5 w-3.5" /> Signature Hope Card — Included FREE</div>
              <div className="space-y-2">
                {addons.map((a) => {
                  const checked = selectedAddons.includes(a.id);
                  return (
                    <label key={a.id} data-testid={`addon-${a.slug}`}
                      className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer transition-colors ${checked ? "border-[#8E9A86] bg-[#8E9A86]/8" : "border-[#E5DFD5] hover:border-[#D1CCC5]"}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleAddon(a.id)} data-testid={`addon-check-${a.slug}`} className="h-4 w-4 accent-[#8E9A86]" />
                      <img src={a.image} alt={a.name} className="h-11 w-11 rounded-lg object-cover bg-[#F2EFEB]" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-[#3A3632]">{a.name}</div>
                        <div className="text-xs text-[#8A8175]">{a.short_desc}</div>
                      </div>
                      <div className="text-[#8E9A86] font-semibold text-sm">+{money(a.price)}</div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center border border-[#D1CCC5] rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="p-3 text-[#3A3632] hover:text-[#8E9A86]"><Minus className="h-4 w-4" /></button>
              <span data-testid="qty-value" className="w-10 text-center font-medium">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="p-3 text-[#3A3632] hover:text-[#8E9A86]"><Plus className="h-4 w-4" /></button>
            </div>
            <button onClick={add} data-testid="add-to-cart-btn" className="flex-1 bg-[#8E9A86] text-white rounded-full px-6 py-3.5 font-medium hover:bg-[#7A8772] transition-colors inline-flex items-center justify-center gap-2">
              <Gift className="h-4 w-4" /> Add to Cart
            </button>
          </div>
          <button onClick={buyNow} data-testid="buy-now-btn" className="w-full mt-3 border border-[#8A8175] text-[#3A3632] rounded-full px-6 py-3.5 font-medium hover:bg-white transition-colors">
            Buy Now
          </button>

          <div className="mt-6 pt-5 border-t border-[#E5DFD5]">
            <div className="gh-overline text-[10px] mb-2">Share this gift</div>
            <ShareButton title={`${p.name} · Hope & Favor`} text={p.short_desc || p.description || `${p.name} from Hope & Favor`} />
          </div>

          {isBox ? (
            <div className="mt-6 space-y-3">
              <p className="flex items-center gap-2 text-sm text-[#8A8175]"><Heart className="h-4 w-4 text-[#C4A4A4]" /> Packaged in a creative box with a gold seal.</p>
              <div className="flex items-start gap-2 text-sm text-[#6B655F] bg-[#8E9A86]/8 rounded-xl p-4" data-testid="shipping-info">
                <Truck className="h-4 w-4 text-[#8E9A86] mt-0.5 shrink-0" />
                <span><span className="font-medium text-[#3A3632]">Shipping:</span> Handmade and shipped within <span className="font-medium">3–5 business days</span>. Estimated delivery <span className="font-medium">5–7 business days</span> after dispatch (U.S.). You'll receive tracking by email.</span>
              </div>
            </div>
          ) : (
            <div className="mt-6 flex items-start gap-2 text-sm text-[#6B655F] bg-[#8E9A86]/8 rounded-xl p-4" data-testid="shipping-info">
              <Truck className="h-4 w-4 text-[#8E9A86] mt-0.5 shrink-0" />
              <span><span className="font-medium text-[#3A3632]">Instant digital download.</span> After checkout you'll receive your printable file by email — nothing ships, so there's no wait.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
